import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeftIcon } from '../../../assets/icons';
import Breadcrumbs from '../../../components/Breadcrumbs';
import { BIND_CHIP, BIND_LABEL, type Binding } from './mockData';
import { usePlatformState } from '../../../features/platform/platformStateSlice';
import {
  useGetTemplateQuery,
  useGetStateDepartmentsQuery,
  useEnableDepartmentMutation,
} from '../../../features/platform/platformApi';

/* ------------------------------------------------------------------ *
 * Platform Admin — one Template's detail (prototype `isTpl`), routed by
 * code: /admin/templates/:code. Levels, default bindings and posts.
 * Posts are read-only (set globally on the template).
 * ------------------------------------------------------------------ */

/** How each level's units are sourced (mirrors the department Territories tab). */
const sourceText = (bind: Binding, name: string) => {
  if (bind === 'anchor') return `LGD › ${name === 'State' || name === 'District' ? name : 'level'}`;
  if (bind === 'inherited') return 'inherited from the anchor above';
  if (bind === 'facility' || bind === 'none') return 'listed one by one';
  return 'members not chosen yet';
};

export default function TemplateDetailPage() {
  const { code: tcode } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { code: stateCode, name: stateName } = usePlatformState();

  const { data, isLoading, isError } = useGetTemplateQuery(tcode!);
  const { data: depts = [] } = useGetStateDepartmentsQuery(stateCode);
  const [enableDepartment] = useEnableDepartmentMutation();

  if (isError || (!isLoading && !data)) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm text-ink-secondary">Template not found.</p>
        <button
          type="button"
          onClick={() => navigate('/admin/templates')}
          className="inline-flex h-10 w-fit items-center gap-1.5 rounded-lg border border-line bg-white pl-2.5 pr-3.5 text-[13px] font-semibold text-ink transition-colors hover:bg-brand-shade"
        >
          <ChevronLeftIcon className="h-3 w-[7px]" />
          Templates
        </button>
      </div>
    );
  }

  if (isLoading || !data) {
    return <p className="text-sm text-ink-secondary">Loading…</p>;
  }

  const enabled = depts.some((d) => d.code === tcode);
  const handleButton = async () => {
    if (enabled) {
      navigate(`/admin/departments/${tcode}`);
      return;
    }
    try {
      await enableDepartment({ code: stateCode, body: { template_code: tcode! } }).unwrap();
      navigate(`/admin/departments/${tcode}`);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="flex flex-col gap-[18px]">
      <Breadcrumbs
        items={[
          { label: 'Template library', to: '/admin/templates' },
          { label: data.name },
        ]}
      />

      <section className="flex flex-wrap items-end justify-between gap-x-8 gap-y-3.5 rounded-xl border border-line/30 bg-white p-6 shadow-card">
        <div className="min-w-0 flex-1 basis-[460px]">
          <div className="text-xs text-ink-secondary">TEMPLATE {tcode} · GLOBAL</div>
          <h1 className="mb-1.5 mt-1 text-2xl font-semibold text-ink">{data.name}</h1>
          <p className="text-sm text-ink-secondary">
            {enabled ? `Enabled in ${stateName}.` : `Not enabled in ${stateName}.`} Control: {data.control}.
          </p>
        </div>
        <button
          type="button"
          onClick={handleButton}
          className={[
            'h-11 rounded-lg px-4 text-[13px] font-semibold transition-colors',
            enabled
              ? 'border border-brand bg-white text-ink hover:bg-brand-shade'
              : 'bg-brand text-white shadow-btn hover:bg-brand-dark',
          ].join(' ')}
        >
          {enabled ? `Open ${stateName} set-up` : `Enable in ${stateName}`}
        </button>
      </section>

      <section className="flex flex-col gap-1.5 rounded-xl border border-line/30 bg-white p-5 shadow-card">
        <h2 className="text-base font-bold text-ink">Levels, default bindings and posts</h2>
        <span className="text-[13px] text-ink-secondary">
          The default each state starts from. Each level can have an official post (appointed, ranked)
          and an elected post (a representative, such as a Sarpanch or Mayor). Changing a post here
          changes it for every state.
        </span>
        <div className="overflow-x-auto">
          <div className="min-w-[1040px]">
            <div className="grid grid-cols-[44px_minmax(140px,1fr)_130px_minmax(170px,1.1fr)_minmax(180px,1.1fr)_minmax(180px,1.1fr)] gap-2.5 border-b border-[#f3f4f6] py-1.5 text-[11px] uppercase tracking-[0.05em] text-ink-secondary">
              <span>Rank</span>
              <span>Level</span>
              <span>Binding</span>
              <span>Source / members</span>
              <span>Official post</span>
              <span>Elected post</span>
            </div>
            {data.levels.map((l, i) => {
              return (
                <div
                  key={i}
                  className="grid grid-cols-[44px_minmax(140px,1fr)_130px_minmax(170px,1.1fr)_minmax(180px,1.1fr)_minmax(180px,1.1fr)] items-center gap-2.5 border-b border-[#f3f4f6] py-2"
                >
                  {/* Rank = the level's position (shown for every level). */}
                  <span className="font-bold text-brand">{l.position}</span>
                  <span className="font-semibold text-ink">{l.name}</span>
                  <span>
                    <span className={['inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold', BIND_CHIP[l.binding]].join(' ')}>
                      {BIND_LABEL[l.binding]}
                    </span>
                  </span>
                  <span className="text-xs text-[#434655]">{sourceText(l.binding, l.name)}</span>
                  <span className={l.official_post ? 'text-ink' : 'text-ink-secondary'}>
                    {l.official_post || 'No official post'}
                  </span>
                  <span className={l.elected_post ? 'text-ink' : 'text-ink-secondary'}>
                    {l.elected_post || 'No elected post'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
